/**
 * The pricing console's quote preview (doc 04-backend/17 §2): what a devotee in one city would
 * actually be quoted for this sheet — fee rules included, optional lines they could untick, and
 * travel marked deferred when it needs an address.
 *
 * **Built from the spec, not a design.** No design frame exists for it; the owner ruled on
 * 2026-10-26 that design-less screens are built from the docs with the console's own components
 * and restyled later.
 *
 * It sits under the sheet preview rather than replacing it: the sheet shows what the version
 * says, this shows what the quote resolver makes of it, and a gap between the two is exactly
 * what an operator needs to see before publishing.
 */
import { useState } from 'react';
import { Chip, Kicker, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { ErrorState, LoadingState } from '../components/feedback';
import { Money, Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { formatInr } from '../lib/money';
import { useQuotePreview } from '../lib/api/hooks/pricing';

export function QuotePreviewSection({ versionId, cityId, cityName }: { versionId: number; cityId: number; cityName: string }) {
  const [drop, setDrop] = useState<string[]>([]);
  const quote = useQuotePreview(versionId, cityId, drop);
  const optional = (quote.data?.lines ?? []).filter((l) => l.is_optional);
  const toggle = (itemType: string) =>
    setDrop(drop.includes(itemType) ? drop.filter((d) => d !== itemType) : [...drop, itemType]);

  return (
    <Section title={`What a devotee in ${cityName} is quoted`} note="Through the quote resolver, fee rules included" pad={false}>
      {quote.isPending && <LoadingState message="Quoting…" />}
      {quote.isError && <ErrorState title="Could not quote this sheet" cause={errorMessage(quote.error)} />}
      {quote.data && (
        <>
          {optional.length > 0 && (
            <div style={{ display: 'flex', gap: 'var(--sp-xs)', flexWrap: 'wrap', alignItems: 'center', padding: 'var(--sp-sm) var(--sp-lg)' }}>
              <Kicker>Optional lines — untick as a devotee would:</Kicker>
              {optional.map((l) => (
                <Chip key={l.item_type} variant="filter" selected={!drop.includes(l.item_type)} label={l.label} onClick={() => toggle(l.item_type)} />
              ))}
            </div>
          )}
          <DataTable
            density="compact"
            columns={[
              { key: 'item', label: 'Line item' },
              { key: 'source', label: 'From' },
              { key: 'amount', label: 'Amount', align: 'right', mono: true },
            ]}
            rows={quote.data.lines.map((line) => ({
              id: `${line.item_type}-${line.sort_order}-${line.fee_rule_id ?? ''}`,
              item: line.dropped ? <s style={{ color: 'var(--c-ink3)' }}>{line.label}</s> : line.label,
              source:
                line.source === 'fee_rule' ? (
                  <StatusChip tone="info" label="fee rule" />
                ) : line.dropped ? (
                  <StatusChip tone="neutral" label="unticked" />
                ) : (
                  <span style={{ color: 'var(--c-ink3)' }}>{line.source === 'city_override' ? 'city price' : line.source.replace(/_/g, ' ')}</span>
                ),
              amount: <Money paise={line.amount_paise} showPaise />,
            }))}
            footer={
              <>
                <span>
                  Quoted total{quote.data.travel_deferred ? ' — travel needs an address, so it is not in this figure' : ''}
                </span>
                <span className="mono-num tabular">{formatInr(quote.data.total_paise, { paise: true })}</span>
              </>
            }
          />
        </>
      )}
    </Section>
  );
}
